import type { Optional } from '@festina-life/core'
import { useQueryClient } from '@tanstack/react-query'
import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import { useNavigationParams } from '../system/useNavigationParams'
import type { MetaModel } from '../types/metaModel'
import { fetchMetaModelKeyPrefix } from '../uiModels/api/keys'
import {
  FALLBACK_BLOCK,
  useFetchBlocks,
} from '../uiModels/api/metaModel/useFetchBlocks'
import { useFetchLayers } from '../uiModels/api/metaModel/useFetchLayers'
import { Provider } from './metaModelContext'
import { MetaModelFetcher } from './MetaModelFetcher'
import { emptyMetaModel, getLayerMetaModelFromMap } from './metaModelHelpers'

type Props = {
  children: ReactNode
}

const MetaModelProvider = ({ children }: Props) => {
  const { server, branch } = useNavigationParams()
  const { data: layers } = useFetchLayers(server)
  const { data: blocks } = useFetchBlocks(server)
  const rootLayer = useMemo(
    () => layers?.[0] ?? { name: null, children: [] },
    [layers],
  )
  const rootBlock = blocks?.at(0) ?? FALLBACK_BLOCK
  const [requestedLayerIds, setRequestedLayerIds] = useState<(string | null)[]>(
    [],
  )
  const [layeredMetaModel, setLayeredMetaModel] = useState<
    Map<Optional<string>, MetaModel>
  >(new Map())
  const queryClient = useQueryClient()

  const getLayerMetaModel = useCallback(
    (layerId: Optional<string>): MetaModel =>
      getLayerMetaModelFromMap({ layeredMetaModel, layerName: layerId }),
    [layeredMetaModel],
  )

  const requestLayerMetaModel = useCallback((layerId: Optional<string>) => {
    setRequestedLayerIds((previousRequestedLayerIds) =>
      Array.from(new Set([...previousRequestedLayerIds, layerId ?? null])),
    )
  }, [])

  const props = useMemo(
    () => ({
      requestLayerMetaModel,
      getLayerMetaModel,
      layerTopology: rootLayer,
      blockTopology: rootBlock,
      requestedLayerIds,
    }),
    [
      getLayerMetaModel,
      requestLayerMetaModel,
      rootLayer,
      rootBlock,
      requestedLayerIds,
    ],
  )

  /** We need to useLayouEffect here instead of useEffect to ensure that layerId requests don't get reset asynchrounously after render. */
  useLayoutEffect(() => {
    setRequestedLayerIds([])
    setLayeredMetaModel(new Map())
    queryClient
      .invalidateQueries({
        queryKey: fetchMetaModelKeyPrefix({
          server,
          branch,
        }),
        refetchType: 'none',
      })
      .catch(() => {
        // Ignore invalidation races during unmount/navigation.
      })
  }, [server, branch, queryClient])

  const handleDidFetch = useCallback(
    (layerId: string | null, fetchedMetaModel: MetaModel) => {
      setLayeredMetaModel((prev) => {
        const newLayeredMetaModel = new Map<Optional<string>, MetaModel>(prev)
        newLayeredMetaModel.set(layerId, fetchedMetaModel ?? emptyMetaModel)
        return newLayeredMetaModel
      })
    },
    [],
  )

  return (
    <Provider value={props}>
      <>
        {requestedLayerIds.map((layerId) => (
          <MetaModelFetcher
            key={layerId ?? 'no-layer'}
            layerId={layerId}
            onDidFetch={handleDidFetch}
          />
        ))}

        {children}
      </>
    </Provider>
  )
}

export { MetaModelProvider }
